import { memo, Suspense, useMemo, useCallback, useEffect } from 'react';

import type { RelevancyCardProps } from '../DynamicCardComponent';
import { usePollGame } from '../hooks/usePollGame';
import { useRelevancyCardType } from '../hooks/useRelevancyCardType';
import { getComponent, useRelevancyFeed } from '../model';
import { useRelevancyOptions } from '../store';
import type { FspEditorialContent, RelevancyCard, RelevancyCardType } from '../types';
import { getNewMainMedia } from '../utils';

import Loader from '@shared/Loader';

const EidosComponent = ({ item, index, cardFormat }: RelevancyCardProps) => {
  const { setCardError } = useRelevancyFeed();
  const { options } = useRelevancyOptions();

  const teaser: FspEditorialContent = useMemo(() => {
    if (!item.teaser) return null;

    const mainMedia = item.teaser.mainMedia ? item.teaser.mainMedia : getNewMainMedia(item.teaser.images);

    return {
      ...item.teaser,
      mainMedia,
    };
  }, [item.teaser]);

  const kind = useMemo(() => teaser?.classification?.kind ?? teaser?.kind ?? item.subType, [item.subType, teaser]);
  const section = useMemo(() => teaser?.classification?.section ?? teaser?.section, [teaser]);
  const itemType = useMemo(() => teaser?.type ?? teaser?.mediaType?.toLowerCase(), [teaser]);

  const { mapPollGame, isPollGame } = usePollGame(options, section);

  const getSubtype = useCallback(() => {
    if (isPollGame) return 'GOTX-HOME-CARD';
    else if (item.subType) return item.subType;
    else if (kind) return kind.replaceAll('_', '-').replaceAll(' ', '-');
  }, [isPollGame, kind, item.subType]);

  const type: RelevancyCardType = useRelevancyCardType({
    itemType,
    isPollGame,
    kind,
    section,
    teaser,
  });

  const mappedItem: RelevancyCard = useMemo(
    () => ({
      ...item,
      teaser: isPollGame ? mapPollGame(section) : teaser,
      subType: getSubtype(),
    }),
    [item, isPollGame, mapPollGame, section, teaser, getSubtype],
  );

  const Component = useMemo(
    () => getComponent<RelevancyCardProps & { eidosType: string }>(type, options.Components),
    [options.Components, type],
  );

  useEffect(() => {
    !Component && setCardError(item, `Component ${itemType} doesn't exists`);
  }, [Component, itemType, item, setCardError]);

  if (!Component) return null;

  return (
    <>
      {Component && (
        <Suspense fallback={<Loader />}>
          <Component item={mappedItem} eidosType={itemType} index={index} cardFormat={cardFormat} />
        </Suspense>
      )}
    </>
  );
};

export default memo(EidosComponent);
